import { Box, Grid, Typography, useMediaQuery, IconButton, Link, TextField  } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { FormValues, Errors, Field  } from "../../routes/Profile/AddSkillBook/AddSkillBook.types";
import { downloadBlobFile } from "../../utils/general";
import { handleFileChange } from "../../utils/skillBook";
import { Icon } from "../Icon/Icon.component";
import { VisuallyHiddenInput } from "../InputCst";
import DataPickerExternalTrainingCst from "../InputCst/DataPickerExternalTrainingCst";




interface RenderFileInputsProps {
  formData: Field[];
  formValues: FormValues;
  errors: Errors;
  t: (key: string) => string;
  MAX_SIZE_UPLOAD: number;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string,
    maxSize: number,
    quality: number,
    errors: Errors,
    setErrors: React.Dispatch<React.SetStateAction<Errors>>,
    setFormValues: React.Dispatch<React.SetStateAction<FormValues>>
  ) => void;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  readOnly?: boolean;
}

export function renderFileInputs({
  formData,
  formValues,
  errors,
  t,
  MAX_SIZE_UPLOAD,
  handleFileChange,
  setErrors,
  setFormValues,
  readOnly
}: RenderFileInputsProps) {
  return formData
    .filter((field) => field.type === "file")
    .map((field) => (
      <Grid key={ field.name } item xs={ 12 }>
        <VisuallyHiddenInput
          name={ field.name }
          label={ formValues[field.name] ? `${ t("file_uploaded") } ${ formValues["file_name"] }` : t(`${field.name}_field`) }
          type={ field.type }
          accept=".pdf, .jpg, .jpeg, .png"
          onChange={ (e) => {
            handleFileChange(
              e,
              field.name,
              MAX_SIZE_UPLOAD,
              1024,
              errors,
              setErrors,
              setFormValues
            );
          }
          }
          error={ !!errors["file"] }
          errorMessage={ t(`${ errors["file"] }`) }
          readOnly={ readOnly }
        />
      </Grid>
    ));
}

interface CertificateSectionProps {
  formData: Field[];
  expanded: boolean;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  handleDateChange: (name: string) => (date: Date | null) => void;
  onToggle: () => void;
  readOnly?: boolean;
  selectedCourse: CourseItem | null;
}

const CertificateSection: React.FC<CertificateSectionProps> = ( {
  formData,
  formValues,
  setFormValues,
  handleDateChange,
  onToggle,
  expanded,
  readOnly,
  selectedCourse
} ) => {

  const { t } = useTranslation();
  const theme = useTheme();
  const MAX_SIZE_UPLOAD = 10 * 1024 * 1024;
  const [errors, setErrors] = React.useState<Errors>({});
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCustomSize = useMediaQuery("(max-width: 1350px)");
  const linkCertificato = formValues.link_certificato as string;
  const hasLink = !!linkCertificato?.trim();
  const canOpen = hasLink;

  return (
    <Box
      sx={ {
        background: theme.customColors.backgroundPrimary,
        borderRadius: "12px",
        mb: 2,
        border: `${theme.customColors.borderAccent} 1px solid`,
        p: 2,
        boxShadow: `${theme.customColors.borderAccent} 0 0 10px`,
        maxHeight: expanded ? undefined : "65px",
        gap: isMobile ? 1 : 0,
        flexDirection: isCustomSize ? "column" : "row"
      } }
    >
      <Box
        onClick={ onToggle }
        aria-pressed={ expanded }
        sx={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          mb: 2
        } }
      >
        <Typography variant="h6" color = { theme.customColors.textPrimary } fontSize={ isMobile ? "1rem" : "1.2rem" } sx={ {  fontWeight: "bold"  } }>
          { t("certificate_section") }
        </Typography>
        <Icon
          icon="dropdown"
          color={ theme.customColors.systemPrimary02 }
          width="24px"
          height="19px"
          style={ {
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .4s linear",
            color: theme.customColors.textWidget
          } }
        />

      </Box>

      { expanded && (
        <Box sx={ {  pb: 2  } }>
          <Grid container spacing={ 2 }>
            <Grid item xs={ 10 }>
              <p style={ {  fontSize: "10px"  } }>{ t("certificate") }</p>
              { renderFileInputs({
                formData,
                formValues,
                t,
                MAX_SIZE_UPLOAD,
                errors,
                setErrors,
                setFormValues,
                handleFileChange,
                readOnly
              }) }
            </Grid>

            {
              (() => {
                const downloadLink = formValues.link_download_certificato;
                if (typeof downloadLink === "string" && downloadLink.trim() !== "") {
                  return (
                    <Grid item xs={ 2 }>
                      <IconButton
                        onClick={ async () => {
                          const url = formValues.link_download_certificato as string;
                          const suggestedName = "certificato.pdf";
                          try {
                            const response = await fetch(url, { method: "GET" });
                            if (!response.ok) {
                              throw new Error(`HTTP error ${response.status}`);
                            }
                            const blob = await response.blob();
                            downloadBlobFile({ fileName: suggestedName, blobFile: blob });
                          } catch (err) {
                            console.error("Errore durante il download del PDF:", err);
                            window.open(url, "_blank");
                          }
                        } }
                        sx={ {
                          position: "relative",
                          top: 40,
                          right: 0,
                          width: 40,
                          height: 40,
                          backgroundColor: theme.customColors.systemPrimary02,
                          borderRadius: "8px",
                          border: "1px solid #333",
                          color: theme.customColors.textPrimary,
                          "&:hover": {
                            backgroundColor: theme.customColors.systemPrimary02
                          }
                        } }
                        aria-label={ t("download_certificate_button") }
                      >
                        <Icon
                          icon="download"
                          size={ 15 }
                        />
                      </IconButton>
                    </Grid>
                  );
                }
                return null;
              })()
            }

            <Grid item xs={ 12 }>
              <p style={ {  fontSize: "10px"  } }>{ t("link_certificate") }</p>
              { (selectedCourse?.status === "APPROVED" || selectedCourse?.status === "PENDING" || selectedCourse?.status === "REJECTED" || hasLink) && selectedCourse?.type ==="Consuntivazione" &&
                <Link
                  underline="none"
                  href={ `${formValues.link_certificato}` }
                  target="_blank"
                  aria-disabled={ !canOpen }
                  sx={ {
                    pointerEvents: !canOpen ? "none" : "auto",
                    color: canOpen
                      ? theme.customColors.textDisabledAlternative
                      : theme.customColors.textPrimary,
                    textDecoration: "underline",
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                      opacity: 1
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: theme.customColors.backgroundPrimary,
                      color: theme.customColors.textPrimary,
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: theme.customColors.border
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: theme.customColors.textPrimary,
                      backgroundColor: theme.customColors.backgroundPrimary,
                      borderRadius: "8px",
                      paddingX: 0.5
                    }
                  } }
                >
                  { hasLink  ? t("open_certificate") : t("link_not_found") }
                </Link>
              }
              { selectedCourse?.type ==="Iscrizione" && selectedCourse?.status === "APPROVED" &&
                <TextField
                  fullWidth
                  name="link_certificato"
                  placeholder="-"
                  variant="outlined"
                  value={ formValues.link_certificato || "" }
                  onChange={ (e) => setFormValues({  ...formValues, link_certificato: e.target.value  }) }
                  disabled={ readOnly }
                  sx={ {
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                      opacity: 1
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: theme.customColors.backgroundPrimary,
                      color: theme.customColors.textPrimary,
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: theme.customColors.border
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: theme.customColors.textPrimary,
                      backgroundColor: theme.customColors.backgroundPrimary,
                      borderRadius: "8px",
                      paddingX: 0.5
                    }
                  } }
                />
              }
            </Grid>

            <Grid item xs={ 12 } sm={ 6 }>
              <p style={ {  fontSize: "10px"  } }>{ t("validity_date_start") }</p>
              <DataPickerExternalTrainingCst
                id="data_inizio_validita"
                label="-"
                name="data_inizio_validita"
                value={
                  typeof formValues.data_inizio_validita === "string" && formValues.data_inizio_validita
                    ? new Date(formValues.data_inizio_validita)
                    : null
                }

                onChange={ handleDateChange("data_inizio_validita") }
                required={ false }
                error={ !!errors["data_inizio_validita"] }
                errorMessage={ errors["data_inizio_validita"] }
                disabled={ readOnly }
              />
            </Grid>

            <Grid item xs={ 12 } sm={ 6 }>
              <p style={ {  fontSize: "10px"  } }>{ t("validity_date_end") }</p>
              <DataPickerExternalTrainingCst
                id="data_fine_validita"
                label="-"
                name="data_fine_validita"
                value={
                  typeof formValues.data_fine_validita === "string" && formValues.data_fine_validita
                    ? new Date(formValues.data_fine_validita)
                    : null
                }
                onChange={ handleDateChange("data_fine_validita") }
                required={ false }
                error={ !!errors["data_fine_validita"] }
                errorMessage={ errors["data_fine_validita"] }
                disabled={ readOnly }
              />
            </Grid>
          </Grid>
        </Box>
      ) }
    </Box>
  );
};

export default CertificateSection;
